<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import {
  fetchDistricts, collectDistrict, upgradeDistrict,
  type District, type SeenDistrict, type OwnedDistrict,
} from '../game/territory-api';
import { districtArt, crestArt } from '../game/art';
import { toast } from '../game/toast';
import cashIcon from '../assets/omerta/icons/cash.webp';
import { districtAccent } from '../game/district-accent';

const emit = defineEmits<{ (e: 'updated'): void; (e: 'goto-map', districtId?: string): void }>();

const loading = ref(true);
const error = ref('');
const districts = ref<District[]>([]);
const busy = ref<string | null>(null);          // districtId currently mutating (collect/upgrade)
const collectAllBusy = ref(false);
const collectPopup = ref<{ id: string; amount: number } | null>(null);

async function refresh() {
  try { districts.value = await fetchDistricts(); }
  catch (e) { error.value = (e as Error).message; }
}

onMounted(async () => {
  await refresh();
  loading.value = false;
});

// ── Intel strip ──────────────────────────────────────────────────────────
const ownedDistricts = computed(() =>
  districts.value.filter((d): d is OwnedDistrict => d.visibility === 'owned'));
const totalCount = computed(() => districts.value.length || 8);
const turfsHeld = computed(() => ownedDistricts.value.length);
const scoutedCount = computed(() => districts.value.filter((d) => d.visibility !== 'unknown').length);
const takeHome = computed(() => ownedDistricts.value.reduce((s, d) => s + d.income, 0));
const readyDistricts = computed(() => ownedDistricts.value.filter((d) => d.readyToCollect));
const readyCashTotal = computed(() => readyDistricts.value.reduce((s, d) => s + d.pendingIncome, 0));

function ownerLabel(o: SeenDistrict['owner']) {
  return o === 'rival' ? 'Rival crew' : 'Up for grabs';
}
function ownerClass(o: SeenDistrict['owner']) {
  return o === 'rival' ? 'text-error border-error/40' : 'text-text-dim border-border';
}
function safePct(d: OwnedDistrict): number {
  return Math.min(100, Math.round((d.pendingIncome / Math.max(1, d.storageCap)) * 100));
}

async function onCollect(d: OwnedDistrict) {
  busy.value = d.districtId; error.value = '';
  try {
    const res = await collectDistrict(d.districtId);
    toast(`Collected ${res.collected.toLocaleString()} cash from ${d.name}`, { icon: cashIcon });
    collectPopup.value = { id: d.districtId, amount: res.collected };
    window.setTimeout(() => {
      if (collectPopup.value?.id === d.districtId) collectPopup.value = null;
    }, 1600);
    await refresh();
    emit('updated');                              // header refetches cash
  } catch (e) { error.value = (e as Error).message; }
  finally { busy.value = null; }
}

async function onUpgrade(d: OwnedDistrict) {
  busy.value = d.districtId; error.value = '';
  try {
    const up = await upgradeDistrict(d.districtId);
    toast(`${d.name} racket upgraded to Lv ${up.level} — ${up.spent.toLocaleString()} cash`,
      { icon: crestArt(d.name) ?? cashIcon });
    await refresh();
    emit('updated');                              // header refetches cash
  } catch (e) { error.value = (e as Error).message; }
  finally { busy.value = null; }
}

/** Sweep every ready-to-collect turf in one go, then a single refresh + emit. */
async function onCollectAll() {
  collectAllBusy.value = true; error.value = '';
  try {
    let total = 0;
    for (const d of readyDistricts.value) {
      total += (await collectDistrict(d.districtId)).collected;
    }
    if (total > 0) toast(`Collected ${total.toLocaleString()} cash across your turf`, { icon: cashIcon });
    await refresh();
    emit('updated');
  } catch (e) { error.value = (e as Error).message; }
  finally { collectAllBusy.value = false; }
}
</script>

<template>
  <div>
    <!-- BANNER -->
    <div class="relative rounded-lg overflow-hidden mb-5 h-36 corner-frame">
      <img v-if="districtArt('The Heights')" :src="districtArt('The Heights')" alt="" class="w-full h-full object-cover" />
      <div v-else class="w-full h-full bg-surface-2"></div>
      <div class="absolute inset-0" style="background:linear-gradient(180deg,rgba(10,7,20,0.25),rgba(10,7,20,0.94))"></div>
      <span class="corner-bracket corner-tl"></span><span class="corner-bracket corner-tr"></span>
      <span class="corner-bracket corner-bl"></span><span class="corner-bracket corner-br"></span>
      <div class="absolute bottom-3 left-4">
        <h1 class="font-heading text-2xl text-gold">Districts</h1>
        <span class="font-label uppercase tracking-wider text-sm text-text-dim block">Run the rackets, hold the turf</span>
      </div>
    </div>

    <!-- INTEL STRIP -->
    <div class="flex flex-wrap items-center gap-2 mb-6">
      <div class="dst-chip">
        <span class="dst-chip-label">Turfs held</span>
        <span class="dst-chip-val text-gold">{{ turfsHeld }}/{{ totalCount }}</span>
      </div>
      <div class="dst-chip">
        <span class="dst-chip-label">Scouted</span>
        <span class="dst-chip-val">{{ scoutedCount }}/{{ totalCount }}</span>
      </div>
      <div class="dst-chip">
        <span class="dst-chip-label">Take-home</span>
        <span class="dst-chip-val text-gold">{{ takeHome.toLocaleString() }}</span>
        <span class="dst-chip-label">cash/hr</span>
      </div>
      <div class="flex-1"></div>
      <button
        v-if="readyDistricts.length"
        class="btn-deco btn-deco--gold whitespace-normal text-center leading-snug"
        :disabled="collectAllBusy"
        @click="onCollectAll"
      >{{ collectAllBusy ? 'Collecting…' : `Collect All — ${readyCashTotal.toLocaleString()} cash waiting` }}</button>
      <span v-if="error" class="text-sm text-error">{{ error }}</span>
    </div>

    <p v-if="loading" class="text-text-dim">Scoping the city…</p>

    <div v-else class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
      <template v-for="d in districts" :key="d.districtId">

        <!-- ═══ UNKNOWN — total fog, nothing scouted yet ═══════════════ -->
        <div v-if="d.visibility === 'unknown'" class="dst-card relative rounded-lg overflow-hidden border border-border/50 bg-surface-2 flex flex-col">
          <div class="relative h-24 w-full overflow-hidden shrink-0">
            <div class="dst-unknown-fog absolute inset-0"></div>
          </div>
          <div class="p-4 flex-1 flex flex-col">
            <div class="flex items-start justify-between gap-3 mb-2">
              <div class="min-w-0">
                <div class="font-label uppercase tracking-wider text-[11px] text-text-dim/60 mb-1">Uncharted Turf</div>
                <h2 class="font-heading text-2xl text-text-dim/80 leading-tight">???</h2>
              </div>
              <div class="dst-unknown-ring shrink-0"></div>
            </div>
            <p class="font-body text-sm text-text-dim/80 italic mb-4">Nobody's walked these streets yet.</p>
            <div class="mt-auto flex">
              <button class="btn-deco btn-deco--ghost btn-deco--sm flex-1" @click="emit('goto-map')">Scout the City Map</button>
            </div>
          </div>
        </div>

        <!-- ═══ SEEN — scouted, basic intel only ═══════════════════════ -->
        <div
          v-else-if="d.visibility === 'seen'"
          class="dst-card dst-liftable relative rounded-lg overflow-hidden border border-border bg-surface flex flex-col"
          :class="d.locked ? 'opacity-80' : ''"
        >
          <div v-if="districtArt(d.name)" class="relative h-24 w-full overflow-hidden">
            <img :src="districtArt(d.name)" :alt="d.name" class="w-full h-full object-cover" style="filter: grayscale(0.55) brightness(0.55)" />
            <div class="absolute inset-0" style="background:linear-gradient(180deg,rgba(10,7,20,0.15),rgba(10,7,20,0.88))"></div>
            <span class="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-label uppercase tracking-wider text-gold bg-bg/80 border border-gold/40">Tier {{ d.tier }}</span>
            <img
              v-if="crestArt(d.name)" :src="crestArt(d.name)" :alt="d.name + ' crest'"
              class="absolute bottom-2 right-2 w-12 h-12 object-contain drop-shadow-lg"
              style="box-shadow: 0 2px 8px rgba(0,0,0,0.6); filter: grayscale(0.4) brightness(0.7)"
            />
          </div>
          <div class="p-4 flex-1 flex flex-col">
            <div class="flex items-start justify-between gap-3 mb-2">
              <div class="min-w-0">
                <h2 class="font-heading text-lg text-text leading-tight truncate">{{ d.name }}</h2>
                <div class="font-label uppercase tracking-wider text-[11px] text-text-dim truncate">{{ d.family }}</div>
              </div>
              <span class="shrink-0 font-label uppercase tracking-wider text-[10px] px-2 py-0.5 rounded-full border" :class="ownerClass(d.owner)">
                {{ ownerLabel(d.owner) }}
              </span>
            </div>

            <div class="dst-stats mb-3" style="grid-template-columns: 1fr 1fr">
              <div class="dst-stat"><b>{{ d.defense }}</b><i>Defense</i></div>
              <div class="dst-stat"><b class="text-text-dim">?</b><i>Income — take it to learn</i></div>
            </div>

            <div class="mt-auto flex gap-2">
              <div v-if="d.locked"
                   class="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-label uppercase tracking-wider rounded border border-border text-text-dim">
                <svg class="shrink-0" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" />
                </svg>
                Locked · reach Lv {{ d.unlockLevel }}
              </div>
              <button
                v-else
                class="btn-deco btn-deco--gold btn-deco--sm flex-1"
                title="Walk onto the district's stronghold on the City Map, then take it in a fight."
                @click="emit('goto-map', d.districtId)"
              >Take it on the map</button>
            </div>
          </div>
        </div>

        <!-- ═══ OWNED — your turf, full operational detail ═════════════ -->
        <div
          v-else
          class="dst-card dst-owned dst-liftable corner-frame relative rounded-lg overflow-hidden bg-surface flex flex-col"
          :style="{ '--rar': districtAccent(d.districtId) }"
        >
          <span class="corner-bracket corner-tl"></span><span class="corner-bracket corner-tr"></span>
          <span class="corner-bracket corner-bl"></span><span class="corner-bracket corner-br"></span>

          <transition name="dst-cash-fade">
            <span v-if="collectPopup?.id === d.districtId" class="dst-cash-popup">+{{ collectPopup.amount.toLocaleString() }} cash</span>
          </transition>

          <div v-if="districtArt(d.name)" class="relative h-24 w-full overflow-hidden">
            <img :src="districtArt(d.name)" :alt="d.name" class="w-full h-full object-cover" />
            <div class="absolute inset-0" style="background:linear-gradient(180deg,rgba(10,7,20,0.05),rgba(10,7,20,0.82))"></div>
            <span class="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-label uppercase tracking-wider text-gold bg-bg/80 border border-gold/40">Tier {{ d.tier }}</span>
            <img
              v-if="crestArt(d.name)" :src="crestArt(d.name)" :alt="d.name + ' crest'"
              class="dst-crest-ring absolute bottom-2 right-2 w-12 h-12 object-contain drop-shadow-lg"
              :style="{ borderColor: districtAccent(d.districtId), boxShadow: '0 2px 8px rgba(0,0,0,0.6)' }"
            />
          </div>

          <div class="p-4 flex-1 flex flex-col">
            <div class="flex items-start justify-between gap-3 mb-2">
              <div class="min-w-0">
                <h2 class="font-heading text-lg text-text leading-tight truncate">{{ d.name }}</h2>
                <div class="font-label uppercase tracking-wider text-[11px] text-text-dim truncate">{{ d.family }}</div>
              </div>
              <span class="shrink-0 font-label uppercase tracking-wider text-[10px] px-2 py-0.5 rounded-full border text-gold border-gold/50">Your Turf</span>
            </div>

            <div class="dst-stats mb-3">
              <div class="dst-stat"><b>Lv {{ d.level }}</b><i>{{ d.racket }}</i></div>
              <div class="dst-stat"><b>{{ d.income }}/hr</b><i>Income</i></div>
              <div class="dst-stat"><b>{{ d.defense }}</b><i>Defense</i></div>
            </div>

            <div class="mb-3">
              <div class="flex items-center justify-between mb-1">
                <span class="font-label uppercase tracking-wider text-[10px] text-text-dim leading-none">In the safe</span>
                <span class="font-body tabular-nums text-xs leading-none" :class="d.atStorageCap ? 'text-error' : 'text-gold'">{{ d.pendingIncome }} / {{ d.storageCap }}</span>
              </div>
              <div class="dst-safe-track">
                <div class="dst-safe-fill" :class="d.atStorageCap ? 'dst-safe-cap' : (d.readyToCollect ? 'dst-safe-ready' : '')" :style="{ width: safePct(d) + '%' }"></div>
              </div>
              <p v-if="d.atStorageCap" class="dst-safe-full-tag mt-1">Safe full — income stalled</p>
            </div>

            <!-- Both plaques are nowrap by design (btn-deco), so the row WRAPS
                 instead of squeezing: when the pair can't share the width, the
                 upgrade drops to its own full-width line — never overflows the
                 card or folds its label into a tower of words. -->
            <div class="mt-auto flex flex-wrap gap-2">
              <button
                class="btn-deco btn-deco--gold btn-deco--sm flex-1"
                :disabled="busy === d.districtId || !d.readyToCollect"
                @click="onCollect(d)"
              >{{ busy === d.districtId ? '…' : 'Collect your cut' }}</button>
              <button
                v-if="!d.maxLevel"
                class="btn-deco btn-deco--ghost btn-deco--sm flex-1"
                :disabled="busy === d.districtId"
                @click="onUpgrade(d)"
              >{{ busy === d.districtId ? '…' : `Upgrade · ${d.upgradeCost?.toLocaleString()} cash` }}</button>
              <div v-else class="dst-maxed-chip flex-1 text-center">Maxed</div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
/* ── Intel strip chips ─────────────────────────────────────────────────── */
.dst-chip {
  display: flex; align-items: center; gap: 6px;
  padding: 6px 14px;
  border: 1px solid var(--color-border, #2a2320);
  border-radius: 999px;
  background: var(--color-surface, #14121a);
}
.dst-chip-label {
  font-family: var(--font-label, sans-serif);
  text-transform: uppercase; letter-spacing: 0.08em; font-size: 10px;
  line-height: 1;
  color: var(--color-text-dim, #9a8f7a);
}
.dst-chip-val { font-family: var(--font-heading, serif); font-size: 15px; line-height: 1; color: var(--color-text, #ece3cf); }

.dst-maxed-chip {
  padding: 8px 14px;
  font-family: var(--font-label, sans-serif);
  text-transform: uppercase; letter-spacing: 0.09em; font-size: 12px;
  color: var(--color-text-dim, #9a8f7a); border: 1px solid var(--color-border, #2a2320); border-radius: 6px;
}

/* ── Stat strip — value over label, no label……value gulf ──────────────── */
.dst-stats {
  display: grid; grid-template-columns: repeat(3, 1fr);
  border: 1px solid var(--color-border, #2a2320);
  border-radius: 8px; overflow: hidden;
  background: linear-gradient(180deg, rgba(255,255,255,0.025), rgba(0,0,0,0.12));
}
.dst-stat {
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 3px;
  padding: 9px 6px; text-align: center; min-width: 0;
}
.dst-stat + .dst-stat { border-left: 1px solid var(--color-border, #2a2320); }
.dst-stat b {
  font-family: var(--font-heading, serif); font-weight: 600;
  font-size: 14px; line-height: 1.15; color: var(--color-text, #ece3cf);
  max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.dst-stat i {
  font-style: normal;
  font-family: var(--font-label, sans-serif);
  font-size: 9px; text-transform: uppercase; letter-spacing: 0.12em; line-height: 1.2;
  color: var(--color-text-dim, #9a8f7a);
}

/* ── Card hover lift (seen + owned only) ──────────────────────────────── */
.dst-card { transition: transform 160ms ease, box-shadow 160ms ease; }
.dst-liftable:hover { transform: translateY(-2px); box-shadow: 0 8px 22px rgba(0,0,0,0.45); }

/* ── UNKNOWN — fog drift + empty crest ring ───────────────────────────── */
.dst-unknown-fog {
  background:
    radial-gradient(circle at 30% 20%, rgba(255,255,255,0.05), transparent 60%),
    radial-gradient(circle at 75% 70%, rgba(255,255,255,0.04), transparent 55%),
    linear-gradient(160deg, #0c0a10, #050408);
  background-size: 180% 180%, 160% 160%, 100% 100%;
  animation: dst-fog-drift 14s ease-in-out infinite;
}
@keyframes dst-fog-drift {
  0%, 100% { background-position: 0% 0%, 100% 100%, 0 0; }
  50% { background-position: 40% 30%, 60% 60%, 0 0; }
}
.dst-unknown-ring {
  width: 44px; height: 44px; border-radius: 999px;
  border: 2px dashed color-mix(in srgb, var(--color-text-dim, #9a8f7a) 45%, transparent);
  background: rgba(0,0,0,0.35);
}

/* ── OWNED — accent rail (mirrors .mkt-tile) + crest pulse + safe meter ── */
.dst-owned {
  border: 1px solid var(--color-border, #2a2320);
}
.dst-crest-ring { animation: dst-crest-pulse 2.6s ease-in-out infinite; }
@keyframes dst-crest-pulse {
  0%, 100% { box-shadow: 0 0 6px 2px color-mix(in srgb, var(--color-gold-bright, #e3c878) 35%, transparent); }
  50% { box-shadow: 0 0 12px 3px color-mix(in srgb, var(--color-gold-bright, #e3c878) 65%, transparent); }
}

.dst-safe-track {
  height: 9px; border-radius: 999px;
  background: rgba(0,0,0,0.45); border: 1px solid rgba(0,0,0,0.4);
  overflow: hidden; position: relative;
}
.dst-safe-fill { height: 100%; border-radius: 999px; transition: width 700ms ease; position: relative; background: var(--color-text-dim, #6b6458); }
.dst-safe-fill.dst-safe-ready { background: linear-gradient(90deg, var(--color-gold, #c8a24c), var(--color-gold-bright, #e3c878)); }
.dst-safe-fill.dst-safe-ready::before {
  content: ''; position: absolute; top: 0; bottom: 0; width: 40%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
  animation: shimmer-sweep 2.2s ease-in-out infinite;
}
.dst-safe-fill.dst-safe-cap {
  background: linear-gradient(90deg, #b3263a, #d9603f);
  animation: dst-safe-cap-pulse 2s ease-in-out infinite;
}
@keyframes dst-safe-cap-pulse {
  0%, 100% { box-shadow: 0 0 4px 1px rgba(179,38,58,0.4); }
  50% { box-shadow: 0 0 10px 2px rgba(217,96,63,0.7); }
}
.dst-safe-full-tag {
  display: inline-block;
  font-family: var(--font-label, sans-serif); font-size: 10px; text-transform: uppercase; letter-spacing: 0.08em;
  color: #ffb4a8; animation: dst-tag-pulse 1.6s ease-in-out infinite;
}
@keyframes dst-tag-pulse { 0%, 100% { opacity: 0.7; } 50% { opacity: 1; } }

/* ── "+N cash" collect popup ───────────────────────────────────────────── */
.dst-cash-popup {
  position: absolute; top: 8px; right: 10px; z-index: 10;
  font-family: var(--font-heading, serif); font-size: 14px; color: var(--color-gold-bright, #e3c878);
  background: rgba(10,7,20,0.9); border: 1px solid var(--color-gold, #c8a24c);
  border-radius: 999px; padding: 2px 10px;
  pointer-events: none;
}
.dst-cash-fade-enter-active { transition: opacity 200ms ease, transform 200ms ease; }
.dst-cash-fade-leave-active { transition: opacity 600ms ease, transform 600ms ease; }
.dst-cash-fade-enter-from { opacity: 0; transform: translateY(4px); }
.dst-cash-fade-leave-to { opacity: 0; transform: translateY(-10px); }

@media (prefers-reduced-motion: reduce) {
  .dst-unknown-fog, .dst-safe-fill.dst-safe-ready::before,
  .dst-safe-fill.dst-safe-cap, .dst-safe-full-tag, .dst-crest-ring,
  .dst-cash-fade-enter-active, .dst-cash-fade-leave-active {
    animation: none !important;
    transition: none !important;
  }
  .dst-liftable:hover { transform: none; }
}
</style>
